import React, { useEffect, useState } from "react";
import { Link, useOutletContext } from "react-router-dom";
import { listMyJobs, getHiringFunnel, getCompanyAttention } from "@/lib/companyApi";
import {
  Briefcase,
  ArrowRight,
  Users,
  Plus,
  BadgeCheck,
  TrendingUp,
  BarChart3,
  Sparkles,
  ClipboardCheck,
  MessageSquare,
  CalendarClock,
  Clock,
  CheckCircle2,
  CreditCard,
} from "lucide-react";

const ATTENTION_META = {
  review:  { icon: Users,      cls: "bg-blue-50 border-blue-200 text-blue-800",    dot: "#0a66c2" },
  credits: { icon: CreditCard, cls: "bg-amber-50 border-amber-200 text-amber-800", dot: "#D97706" },
  verify:  { icon: BadgeCheck, cls: "bg-stone-50 border-stone-200 text-stone-700", dot: "#57534E" },
};

function scoreColor(s) {
  return s >= 70 ? "#15803D" : s >= 40 ? "#D97706" : "#C2410C";
}

const STAGE_COLORS = {
  applied: "#6B7280",
  screening: "#3B82F6",
  interview: "#8B5CF6",
  offer: "#F59E0B",
  hired: "#10B981",
  rejected: "#EF4444",
};

const STAGE_LABELS = {
  applied: "Applied",
  screening: "Screening",
  interview: "Interview",
  offer: "Offer",
  hired: "Hired",
  rejected: "Rejected",
};

const QUICK_ACTIONS = [
  { to: "/company/jobs/new", label: "Post a job", sub: "Live in 60 seconds", icon: Plus, accent: "#C2410C" },
  { to: "/company/talent", label: "Browse talent", sub: "Ranked by score", icon: Sparkles, accent: "#7C3AED" },
  { to: "/company/scorecards", label: "Scorecards", sub: "Structure interviews", icon: ClipboardCheck, accent: "#0a66c2" },
  { to: "/company/schedule", label: "Schedule", sub: "Book interviews", icon: CalendarClock, accent: "#15803D" },
];

export default function CompanyDashboard() {
  const { company } = useOutletContext() || {};
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [funnel, setFunnel] = useState(null);
  const [funnelLoading, setFunnelLoading] = useState(true);
  const [attention, setAttention] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await listMyJobs();
        setJobs(data);
      } finally {
        setLoading(false);
      }
    })();

    (async () => {
      try {
        const { data } = await getHiringFunnel();
        setFunnel(data);
      } catch (e) {
        // no apps yet — fine
      } finally {
        setFunnelLoading(false);
      }
    })();

    getCompanyAttention().then(({ data }) => setAttention(data)).catch(() => {});
  }, []);

  const totalApplicants = jobs.reduce((s, j) => s + (j.applications_count || 0), 0);

  return (
    <div className="container-x py-10 md:py-14">
      <div className="mb-10 flex flex-col md:flex-row md:items-end justify-between gap-6">
        <div>
          <span className="overline">Recruiter workspace</span>
          <h1 className="font-display text-4xl md:text-5xl mt-3 leading-tight">
            {(company?.name || "Welcome").split(" ")[0]}, your hiring pipeline{" "}
            <span className="italic text-stone-500">at a glance.</span>
          </h1>
        </div>
        <Link to="/company/jobs/new" data-testid="dash-post-job" className="btn-primary">
          <Plus size={16} /> Post a job
        </Link>
      </div>

      {/* Hero stats — now interactive cards with CTAs */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 mb-10">
        <StatCard
          to="/company/jobs"
          label="Open jobs"
          value={company?.stats?.jobs_open ?? 0}
          icon={Briefcase}
          accent="#C2410C"
          cta="Manage jobs"
        />
        <StatCard
          to="/company/talent"
          label="Total applicants"
          value={totalApplicants}
          icon={Users}
          accent="#7C3AED"
          cta="View talent pool"
        />
        <StatCard
          to="/company/analytics"
          label="Conversion"
          value={funnel?.conversion_rate != null ? `${funnel.conversion_rate}%` : "—"}
          icon={TrendingUp}
          accent="#15803D"
          cta="See analytics"
        />
        <StatCard
          to="/company/settings"
          label="Status"
          value={company?.verified ? "Verified" : "Pending"}
          icon={BadgeCheck}
          accent={company?.verified ? "#15803D" : "#D97706"}
          cta={company?.verified ? "View profile" : "Get verified"}
          subValue={company?.created_at ? `${age(company.created_at)} old` : null}
        />
      </div>

      {/* Needs your attention */}
      {attention && (
        <div className="mb-10" data-testid="co-attention">
          <h2 className="font-display text-2xl mb-4">Needs your attention</h2>
          {attention.actions.length === 0 ? (
            <div className="rounded-3xl border border-emerald-200 bg-emerald-50 p-6 flex items-center gap-3 text-emerald-800 text-sm">
              <CheckCircle2 size={20} /> All clear — no applicants waiting, credits are healthy.
            </div>
          ) : (
            <div className="space-y-2.5">
              {attention.actions.map((a, i) => {
                const m = ATTENTION_META[a.kind] || ATTENTION_META.verify;
                return (
                  <Link key={i} to={a.to} className={`flex items-center gap-3 rounded-2xl border px-4 py-3.5 text-sm font-medium hover:-translate-y-0.5 transition-all ${m.cls}`}>
                    <m.icon size={17} style={{ color: m.dot }} />
                    <span className="flex-1">{a.label}</span>
                    <ArrowRight size={15} className="opacity-60" />
                  </Link>
                );
              })}
            </div>
          )}

          {/* Top new candidates awaiting review */}
          {attention.top_new?.length > 0 && (
            <div className="mt-6 rounded-3xl border border-stone-200 bg-white overflow-hidden" data-testid="co-top-new">
              <div className="px-5 py-3.5 border-b border-stone-100 flex items-center gap-2">
                <Sparkles size={15} className="text-[#C2410C]" />
                <span className="text-sm font-semibold text-stone-800">Best new candidates waiting for you</span>
              </div>
              {attention.top_new.map((c, i) => (
                <Link key={c.application_id} to={`/company/jobs/${c.job_id}`}
                  className={`flex items-center gap-4 px-5 py-3.5 hover:bg-stone-50 ${i > 0 ? "border-t border-stone-50" : ""}`}>
                  <span className="font-mono text-lg font-bold w-10 text-center" style={{ color: scoreColor(c.wishire_score) }}>{c.wishire_score}</span>
                  <div className="min-w-0 flex-1">
                    <p className="font-medium text-stone-900 truncate">{c.candidate_name}</p>
                    <p className="text-xs text-stone-500 truncate">{c.job_title}{c.screening_score != null ? ` · screening ${c.screening_score}` : ""}</p>
                  </div>
                  <span className="text-xs text-stone-400 shrink-0">{c.applied_at ? new Date(c.applied_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : ""}</span>
                  <ArrowRight size={14} className="text-stone-300 shrink-0" />
                </Link>
              ))}
            </div>
          )}
        </div>
      )}

      {/* Quick actions */}
      <div className="mb-10">
        <p className="overline mb-3">Quick actions</p>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          {QUICK_ACTIONS.map((a) => (
            <Link
              key={a.to}
              to={a.to}
              data-testid={`dash-qa-${a.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="group flex items-center gap-3 rounded-2xl border border-stone-200 bg-white p-4 hover:border-stone-900 hover:shadow-[0_18px_40px_-24px_rgba(28,25,23,0.4)] transition-all"
            >
              <span
                className="inline-flex p-2.5 rounded-xl shrink-0 transition-transform group-hover:scale-110"
                style={{ background: `${a.accent}15`, color: a.accent }}
              >
                <a.icon size={18} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold text-stone-900 truncate">{a.label}</p>
                <p className="text-xs text-stone-500 truncate">{a.sub}</p>
              </div>
              <ArrowRight size={15} className="ml-auto text-stone-300 group-hover:text-stone-900 group-hover:translate-x-0.5 transition-all shrink-0" />
            </Link>
          ))}
        </div>
      </div>

      {/* Hiring Funnel Analytics */}
      <div className="mb-10">
        <div className="flex items-center gap-3 mb-5">
          <BarChart3 size={18} className="text-[#C2410C]" />
          <h2 className="font-display text-2xl">Hiring funnel</h2>
          {funnel && funnel.total_applications > 0 && (
            <Link to="/company/analytics" className="ml-auto text-sm text-[#C2410C] hover:text-stone-900 flex items-center gap-1">
              Full analytics <ArrowRight size={14} />
            </Link>
          )}
        </div>

        {funnelLoading ? (
          <div className="rounded-3xl bg-white border border-stone-200 p-7 text-stone-500">Loading funnel…</div>
        ) : !funnel || funnel.total_applications === 0 ? (
          <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-8 text-center">
            <p className="text-stone-500 text-sm mb-4">
              No applications yet — funnel data will appear once candidates apply.
            </p>
            <Link to="/company/jobs/new" className="btn-primary inline-flex">
              <Plus size={16} /> Post your first job
            </Link>
          </div>
        ) : (
          <div className="rounded-3xl bg-white border border-stone-200 p-7 space-y-4">
            <div className="flex items-center justify-between text-sm mb-2">
              <span className="text-stone-500 flex items-center gap-1.5">
                <TrendingUp size={14} className="text-emerald-700" />
                {funnel.conversion_rate}% conversion
              </span>
              <span className="text-stone-500 font-mono">{funnel.total_applications} total applications</span>
            </div>
            {Object.entries(funnel.funnel).map(([stage, count]) => {
              const pct = funnel.total_applications > 0
                ? Math.round((count / funnel.total_applications) * 100)
                : 0;
              return (
                <div key={stage}>
                  <div className="flex items-center justify-between mb-1.5">
                    <span className="text-sm font-medium text-stone-700">{STAGE_LABELS[stage]}</span>
                    <span className="text-sm text-stone-500 font-mono">{count} <span className="text-stone-400">({pct}%)</span></span>
                  </div>
                  <div className="h-2.5 bg-stone-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full transition-all duration-700"
                      style={{ width: `${Math.max(pct, 1)}%`, background: STAGE_COLORS[stage] }}
                    />
                  </div>
                </div>
              );
            })}

            {/* Per-job breakdown */}
            {funnel.job_stats?.length > 0 && (
              <div className="pt-4 border-t border-stone-100">
                <p className="overline mb-3">Per job</p>
                <div className="space-y-2">
                  {funnel.job_stats.slice(0, 5).map((j) => (
                    <Link
                      key={j.job_id}
                      to={`/company/jobs/${j.job_id}`}
                      className="flex items-center gap-3 text-sm rounded-lg -mx-2 px-2 py-1.5 hover:bg-stone-50 transition-colors"
                    >
                      <span className="flex-1 truncate text-stone-700">{j.title}</span>
                      <span className="text-stone-500 font-mono">{j.total} apps</span>
                      <span className="text-emerald-700 font-mono">{j.hired} hired</span>
                      {j.avg_score > 0 && (
                        <span className="text-[#C2410C] font-mono">~{j.avg_score} score</span>
                      )}
                      <ArrowRight size={13} className="text-stone-300" />
                    </Link>
                  ))}
                </div>
              </div>
            )}

            {/* Source breakdown */}
            {funnel.source_breakdown && Object.keys(funnel.source_breakdown).length > 0 && (
              <div className="pt-4 border-t border-stone-100">
                <p className="overline mb-3">Source breakdown</p>
                <SourceBreakdown breakdown={funnel.source_breakdown} total={funnel.total_applications} />
              </div>
            )}
          </div>
        )}
      </div>

      {/* Jobs */}
      <div className="flex items-center justify-between mb-5">
        <h2 className="font-display text-2xl">Your jobs</h2>
        <Link to="/company/jobs" className="text-sm text-[#C2410C] hover:text-stone-900 inline-flex items-center gap-1">
          See all <ArrowRight size={14} />
        </Link>
      </div>

      {loading ? (
        <p className="text-stone-500">Loading…</p>
      ) : jobs.length === 0 ? (
        <EmptyJobs />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {jobs.slice(0, 6).map((j) => (
            <JobCard key={j.job_id} job={j} />
          ))}
        </div>
      )}

      {/* Talent pool callout */}
      <div className="mt-12 rounded-3xl bg-stone-900 text-stone-50 p-8 md:p-10 relative overflow-hidden wsh-grain">
        <div
          className="absolute inset-0 opacity-40"
          style={{
            background:
              "radial-gradient(500px 320px at 90% 10%, rgba(217,119,6,0.4), transparent 60%)",
          }}
        />
        <div className="relative grid grid-cols-1 md:grid-cols-3 gap-8 items-center">
          <div className="md:col-span-2">
            <span className="overline" style={{ color: "rgba(253,251,247,0.55)" }}>
              Talent pool
            </span>
            <h3 className="font-display text-3xl mt-2">
              Skip the inbox.{" "}
              <span className="italic text-amber-300">Browse candidates by Wishire Score.</span>
            </h3>
            <p className="mt-3 text-stone-300 max-w-xl">
              Pre-ranked by composite resume + voice interview + skills score. Reach out directly to
              the top decile for your target role.
            </p>
          </div>
          <Link
            to="/company/talent"
            data-testid="dash-go-talent"
            className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full bg-amber-300 text-stone-900 hover:bg-amber-200 transition-colors font-medium"
          >
            Open talent pool <ArrowRight size={16} />
          </Link>
        </div>
      </div>
    </div>
  );
}

function StatCard({ to, label, value, icon: Icon, accent, cta, subValue }) {
  return (
    <Link
      to={to}
      data-testid={`dash-stat-${label.toLowerCase().replace(/\s+/g, "-")}`}
      className="group relative flex flex-col justify-between rounded-3xl border border-stone-200 bg-white p-6 overflow-hidden hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.3)] transition-all duration-300"
    >
      <div
        className="absolute -top-8 -right-8 w-28 h-28 rounded-full opacity-[0.07] group-hover:opacity-[0.12] transition-opacity"
        style={{ background: accent }}
      />
      <div className="relative flex items-start justify-between">
        <span
          className="inline-flex p-2.5 rounded-xl"
          style={{ background: `${accent}15`, color: accent }}
        >
          <Icon size={20} />
        </span>
      </div>
      <div className="relative mt-6">
        <p className="font-display text-4xl leading-none text-stone-900">{value}</p>
        <p className="overline mt-2 flex items-center gap-2">
          {label}
          {subValue && (
            <span className="inline-flex items-center gap-1 normal-case tracking-normal text-stone-400 font-mono text-[10px]">
              <Clock size={10} /> {subValue}
            </span>
          )}
        </p>
      </div>
      <span
        className="relative mt-4 inline-flex items-center gap-1 text-sm font-medium opacity-70 group-hover:opacity-100 transition-opacity"
        style={{ color: accent }}
      >
        {cta} <ArrowRight size={14} className="group-hover:translate-x-0.5 transition-transform" />
      </span>
    </Link>
  );
}

function JobCard({ job }) {
  return (
    <div
      data-testid={`dash-job-${job.job_id}`}
      className="flex flex-col rounded-3xl border border-stone-200 bg-white p-6 hover:-translate-y-1 hover:shadow-[0_24px_60px_-30px_rgba(28,25,23,0.25)] transition-all duration-300"
    >
      <div className="flex items-center justify-between">
        <span className={`px-2.5 py-1 rounded-full font-mono text-[10px] uppercase tracking-widest border ${statusStyle(job.status)}`}>
          {job.status}
        </span>
        <span className="overline">{job.location || (job.remote ? "Remote" : "—")}</span>
      </div>
      <h3 className="font-display text-xl mt-4 leading-tight">{job.title}</h3>
      <p className="text-stone-500 text-sm mt-1">{job.target_role}</p>
      <div className="mt-4 flex items-center gap-2 text-sm text-stone-700">
        <Users size={14} /> {job.applications_count || 0} applicants
      </div>
      {/* CTA row */}
      <div className="mt-5 pt-4 border-t border-stone-100 flex items-center gap-2">
        <Link
          to={`/company/jobs/${job.job_id}`}
          className="flex-1 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full bg-stone-900 text-stone-50 hover:bg-[#C2410C] transition-colors text-sm font-medium"
        >
          View pipeline <ArrowRight size={14} />
        </Link>
        <Link
          to={`/company/jobs/${job.job_id}?tab=applicants`}
          className="inline-flex items-center justify-center gap-1.5 px-3 py-2.5 rounded-full border border-stone-300 text-stone-700 hover:border-stone-900 hover:text-stone-900 transition-colors text-sm font-medium"
          title="Review applicants"
        >
          <MessageSquare size={14} />
        </Link>
      </div>
    </div>
  );
}

function EmptyJobs() {
  return (
    <div className="rounded-3xl border border-dashed border-stone-300 bg-[#F5F3ED] p-10 text-center">
      <div className="inline-flex p-3 rounded-2xl bg-white text-[#C2410C] mb-4">
        <Briefcase size={20} />
      </div>
      <h3 className="font-display text-2xl">Your first job is one click away.</h3>
      <p className="mt-2 text-stone-600 max-w-md mx-auto">
        Post in under 60 seconds. We auto-generate copy-paste posts for LinkedIn, Naukri, Indeed and Twitter.
      </p>
      <Link to="/company/jobs/new" className="btn-primary mt-6 inline-flex">
        <Plus size={16} /> Post a job
      </Link>
    </div>
  );
}

function statusStyle(status) {
  if (status === "open") return "bg-emerald-50 border-emerald-200 text-emerald-800";
  if (status === "draft") return "bg-stone-100 border-stone-300 text-stone-700";
  return "bg-amber-50 border-amber-200 text-amber-800";
}

function age(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  const days = Math.max(1, Math.round((Date.now() - d.getTime()) / (24 * 3600 * 1000)));
  if (days < 30) return `${days}d`;
  const months = Math.round(days / 30);
  return `${months}mo`;
}

const SOURCE_COLORS = {
  wishire: "#C2410C",
  linkedin: "#0a66c2",
  naukri: "#4a154b",
  indeed: "#2557a7",
  internshala: "#007bff",
  shine: "#e6492d",
};

function sourceColor(src) {
  return SOURCE_COLORS[src] || "#78716C";
}

function SourceBreakdown({ breakdown, total }) {
  const sorted = Object.entries(breakdown).sort((a, b) => b[1] - a[1]);
  return (
    <div className="space-y-2.5">
      {sorted.map(([src, count]) => {
        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
        const label = src.charAt(0).toUpperCase() + src.slice(1);
        return (
          <div key={src}>
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-stone-700">{label}</span>
              <span className="text-sm text-stone-500 font-mono">
                {count} <span className="text-stone-400">({pct}%)</span>
              </span>
            </div>
            <div className="h-2 bg-stone-100 rounded-full overflow-hidden">
              <div
                className="h-full rounded-full transition-all duration-700"
                style={{ width: `${Math.max(pct, 1)}%`, background: sourceColor(src) }}
              />
            </div>
          </div>
        );
      })}
    </div>
  );
}
